import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CodeIcon from "@mui/icons-material/Code";
import { CodeBlock, dracula } from "react-code-blocks";
import { getWebhookDomain, hostname } from "../../../utils";
import {
  PIXEL_STATUS_ACTIVE,
  PIXEL_STATUS_PAUSED,
  PIXEL_TAG_TYPE_SCRIPT,
  PIXEL_TAG_TYPE_PIXEL,
  PIXEL_TAG_TYPE_HTML,
  PIXEL_TAG_TYPE_CUSTOM,
  PIXEL_TAG_POSITION_HEAD,
  PIXEL_TAG_POSITION_BODY_START,
  PIXEL_TAG_POSITION_BODY_END,
} from "../../../constants";
import { useState, useEffect } from "react";
import axios from "axios";

interface PixelDialogProps {
  open: boolean;
  onClose: () => void;
  listId: string;
  listName: string;
  companyId: string;
  getToken: () => Promise<string>;
}


interface Pixel {
  id: string;
  companyId: string;
  listId: string | null;
  listName: string | null;
  pixelId: string;
  websiteName: string;
  websiteUrl: string;
  webhookUrl: string;
  installUrl: string | null;
  status: string;
  hitCount: number;
  leadCount: number;
  lastActivityAt: string | null;
  createdDate: string;
}

interface PixelTag {
  id: string;
  pixelId: string;
  name: string;
  tagType: string;
  content: string;
  isUrl: boolean;
  position: string;
  loadOrder: number;
  enabled: boolean;
  triggerConditions: string | null;
  createdDate: string;
  updatedDate: string;
}

const PixelDialog: React.FC<PixelDialogProps> = ({
  open,
  onClose,
  listId,
  listName,
  companyId,
  getToken,
}) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [websiteName, setWebsiteName] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pixelData, setPixelData] = useState<Pixel | null>(null);
  const [copied, setCopied] = useState(false);

  // Manage tab state
  const [pixels, setPixels] = useState<Pixel[]>([]);
  const [loadingPixels, setLoadingPixels] = useState(false);
  const [pixelsError, setPixelsError] = useState<string | null>(null);

  // Tag management state
  const [selectedPixel, setSelectedPixel] = useState<Pixel | null>(null);
  const [tags, setTags] = useState<PixelTag[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [showTagForm, setShowTagForm] = useState(false);
  const [editingTag, setEditingTag] = useState<PixelTag | null>(null);

  // Tag form state
  const [tagName, setTagName] = useState("");
  const [tagType, setTagType] = useState(PIXEL_TAG_TYPE_SCRIPT);
  const [tagContent, setTagContent] = useState("");
  const [tagIsUrl, setTagIsUrl] = useState(false);
  const [tagPosition, setTagPosition] = useState(PIXEL_TAG_POSITION_HEAD);
  const [tagEnabled, setTagEnabled] = useState(true);
  const [savingTag, setSavingTag] = useState(false);

  // Confirm dialog state
  const [deleteConfirmPixel, setDeleteConfirmPixel] = useState<Pixel | null>(null);
  const [deleteConfirmTag, setDeleteConfirmTag] = useState<PixelTag | null>(null);

  const webhookUrl = `${getWebhookDomain()}/lists/${listId}/pixel/webhook`;

  // Fetch pixels when dialog opens or tab changes to manage
  useEffect(() => {
    if (open && currentTab === 1) {
      fetchPixels();
    }
  }, [open, currentTab]);

  // Reset tag management when pixel is deselected
  useEffect(() => {
    if (!selectedPixel) {
      setTags([]);
      setShowTagForm(false);
      setEditingTag(null);
    }
  }, [selectedPixel]);

  const fetchPixels = async () => {
    setLoadingPixels(true);
    setPixelsError(null);
    try {
      const token = await getToken();
      // Fetch all company pixels (not just for this list)
      const response = await axios.get(
        `${hostname}/pixels?companyId=${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setPixels(response.data);
    } catch (err: any) {
      setPixelsError(err.message || "Failed to load pixels");
    } finally {
      setLoadingPixels(false);
    }
  };

  const fetchTags = async (pixelId: string) => {
    setLoadingTags(true);
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/pixels/${pixelId}/tags?companyId=${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setTags(response.data);
    } catch (err: any) {
      console.error("Failed to load tags:", err);
    } finally {
      setLoadingTags(false);
    }
  };

  const handleCreate = async () => {
    if (!websiteName.trim() || !websiteUrl.trim()) {
      setError("Please fill in all required fields");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getToken();

      // Create pixel - single API call handles AudienceLab + local storage
      const response = await axios.post(
        `${hostname}/pixels`,
        {
          companyId,
          listId,
          websiteName: websiteName.trim(),
          websiteUrl: websiteUrl.trim(),
          webhookUrl: webhookUrl,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setPixelData(response.data);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        "An error occurred while creating the pixel";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setWebsiteName("");
    setWebsiteUrl("");
    setError(null);
    setPixelData(null);
    setCopied(false);
    setCurrentTab(0);
    setSelectedPixel(null);
    setShowTagForm(false);
    setEditingTag(null);
    onClose();
  };

  const handleToggleStatus = async (pixel: Pixel) => {
    try {
      const token = await getToken();
      const newStatus =
        pixel.status === PIXEL_STATUS_ACTIVE
          ? PIXEL_STATUS_PAUSED
          : PIXEL_STATUS_ACTIVE;
      await axios.put(
        `${hostname}/pixels/${pixel.id}`,
        {
          companyId,
          status: newStatus,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchPixels();
    } catch (err: any) {
      setPixelsError(err.message || "Failed to update pixel status");
    }
  };

  const handleLinkToList = async (pixel: Pixel) => {
    try {
      const token = await getToken();
      // If pixel is already linked to this list, unlink it; otherwise link it
      const newListId = pixel.listId === listId ? null : listId;
      await axios.put(
        `${hostname}/pixels/${pixel.id}`,
        {
          companyId,
          listId: newListId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchPixels();
    } catch (err: any) {
      setPixelsError(err.message || "Failed to update pixel");
    }
  };

  const handleDeletePixel = async (pixel: Pixel) => {
    try {
      const token = await getToken();
      await axios.delete(
        `${hostname}/pixels/${pixel.id}?companyId=${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchPixels();
      if (selectedPixel?.id === pixel.id) {
        setSelectedPixel(null);
      }
    } catch (err: any) {
      setPixelsError(err.message || "Failed to delete pixel");
    } finally {
      setDeleteConfirmPixel(null);
    }
  };

  const handleManageTags = (pixel: Pixel) => {
    setSelectedPixel(pixel);
    fetchTags(pixel.id);
  };

  const handleBackToPixels = () => {
    setSelectedPixel(null);
    setShowTagForm(false);
    setEditingTag(null);
  };

  const resetTagForm = () => {
    setTagName("");
    setTagType(PIXEL_TAG_TYPE_SCRIPT);
    setTagContent("");
    setTagIsUrl(false);
    setTagPosition(PIXEL_TAG_POSITION_HEAD);
    setTagEnabled(true);
    setEditingTag(null);
  };

  const handleEditTag = (tag: PixelTag) => {
    setEditingTag(tag);
    setTagName(tag.name);
    setTagType(tag.tagType);
    setTagContent(tag.content);
    setTagIsUrl(tag.isUrl);
    setTagPosition(tag.position);
    setTagEnabled(tag.enabled);
    setShowTagForm(true);
  };

  const handleSaveTag = async () => {
    if (!tagName.trim() || !tagContent.trim() || !selectedPixel) {
      return;
    }

    setSavingTag(true);
    try {
      const token = await getToken();

      const tagData = {
        companyId,
        name: tagName.trim(),
        tagType,
        content: tagContent.trim(),
        isUrl: tagIsUrl,
        position: tagPosition,
        enabled: tagEnabled,
      };

      if (editingTag) {
        await axios.put(
          `${hostname}/pixels/${selectedPixel.id}/tags/${editingTag.id}`,
          tagData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else {
        await axios.post(
          `${hostname}/pixels/${selectedPixel.id}/tags`,
          tagData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      await fetchTags(selectedPixel.id);
      setShowTagForm(false);
      resetTagForm();
    } catch (err: any) {
      console.error("Failed to save tag:", err);
    } finally {
      setSavingTag(false);
    }
  };

  const handleDeleteTag = async (tag: PixelTag) => {
    if (!selectedPixel) return;

    try {
      const token = await getToken();
      await axios.delete(
        `${hostname}/pixels/${selectedPixel.id}/tags/${tag.id}?companyId=${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      await fetchTags(selectedPixel.id);
    } catch (err: any) {
      console.error("Failed to delete tag:", err);
    } finally {
      setDeleteConfirmTag(null);
    }
  };

  const handleToggleTagEnabled = async (tag: PixelTag) => {
    if (!selectedPixel) return;

    try {
      const token = await getToken();
      await axios.put(
        `${hostname}/pixels/${selectedPixel.id}/tags/${tag.id}`,
        {
          companyId,
          enabled: !tag.enabled,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      await fetchTags(selectedPixel.id);
    } catch (err: any) {
      console.error("Failed to toggle tag:", err);
    }
  };

  // Get the tag manager loader URL using the pixel's row ID
  const getTagManagerLoaderUrl = (pixelRowId: string) => {
    return `${getWebhookDomain()}/public/pixel/${pixelRowId}/loader.js`;
  };

  const getInstallScript = () => {
    if (!pixelData) return "";
    // Use the pixel's row ID for the tag manager loader
    const loaderUrl = getTagManagerLoaderUrl(pixelData.id);
    return `<script src="${loaderUrl}" async></script>`;
  };

  const getInstallScriptForPixel = (pixel: Pixel) => {
    // Use the pixel's row ID for the tag manager loader
    const loaderUrl = getTagManagerLoaderUrl(pixel.id);
    return `<script src="${loaderUrl}" async></script>`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString();
  };

  const getTagTypeLabel = (type: string) => {
    switch (type) {
      case PIXEL_TAG_TYPE_SCRIPT:
        return "Script";
      case PIXEL_TAG_TYPE_PIXEL:
        return "Tracking Pixel";
      case PIXEL_TAG_TYPE_HTML:
        return "HTML";
      case PIXEL_TAG_TYPE_CUSTOM:
        return "Custom";
      default:
        return type;
    }
  };

  const getPositionLabel = (position: string) => {
    switch (position) {
      case PIXEL_TAG_POSITION_HEAD:
        return "Head";
      case PIXEL_TAG_POSITION_BODY_START:
        return "Body Start";
      case PIXEL_TAG_POSITION_BODY_END:
        return "Body End";
      default:
        return position;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {selectedPixel ? (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton onClick={handleBackToPixels} size="small">
                <CloseIcon />
              </IconButton>
              Tag Manager - {selectedPixel.websiteName}
            </Box>
          </>
        ) : (
          "Website Leads Pixel"
        )}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {selectedPixel ? (
          // Tag Management View
          <>
            {showTagForm ? (
              // Tag Form
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {editingTag ? "Edit Tag" : "Add New Tag"}
                </Typography>

                <TextField
                  label="Tag Name"
                  value={tagName}
                  onChange={(e) => setTagName(e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                  placeholder="e.g., Google Analytics, Facebook Pixel"
                />

                <FormControl fullWidth margin="normal">
                  <InputLabel>Tag Type</InputLabel>
                  <Select
                    value={tagType}
                    label="Tag Type"
                    onChange={(e) => setTagType(e.target.value)}
                  >
                    <MenuItem value={PIXEL_TAG_TYPE_SCRIPT}>Script</MenuItem>
                    <MenuItem value={PIXEL_TAG_TYPE_PIXEL}>Tracking Pixel</MenuItem>
                    <MenuItem value={PIXEL_TAG_TYPE_HTML}>HTML</MenuItem>
                    <MenuItem value={PIXEL_TAG_TYPE_CUSTOM}>Custom</MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={tagIsUrl}
                      onChange={(e) => setTagIsUrl(e.target.checked)}
                    />
                  }
                  label="Load from URL (external script)"
                  sx={{ mt: 1, mb: 1 }}
                />

                <TextField
                  label={tagIsUrl ? "Script URL" : "Code Content"}
                  value={tagContent}
                  onChange={(e) => setTagContent(e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                  multiline={!tagIsUrl}
                  rows={tagIsUrl ? 1 : 6}
                  placeholder={
                    tagIsUrl
                      ? "https://www.googletagmanager.com/gtag/js?id=GA_MEASUREMENT_ID"
                      : "<script>\n  // Your tracking code here\n</script>"
                  }
                />

                <FormControl fullWidth margin="normal">
                  <InputLabel>Position</InputLabel>
                  <Select
                    value={tagPosition}
                    label="Position"
                    onChange={(e) => setTagPosition(e.target.value)}
                  >
                    <MenuItem value={PIXEL_TAG_POSITION_HEAD}>Head (recommended)</MenuItem>
                    <MenuItem value={PIXEL_TAG_POSITION_BODY_START}>Body Start</MenuItem>
                    <MenuItem value={PIXEL_TAG_POSITION_BODY_END}>Body End</MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={tagEnabled}
                      onChange={(e) => setTagEnabled(e.target.checked)}
                    />
                  }
                  label="Enabled"
                  sx={{ mt: 1 }}
                />

                <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                  <Button
                    onClick={() => {
                      setShowTagForm(false);
                      resetTagForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveTag}
                    disabled={savingTag || !tagName.trim() || !tagContent.trim()}
                  >
                    {savingTag ? <CircularProgress size={24} /> : editingTag ? "Update Tag" : "Add Tag"}
                  </Button>
                </Box>
              </Box>
            ) : (
              // Tag List View
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Add tracking scripts, pixels, and custom code that will be dynamically loaded on websites using this pixel.
                  Changes take effect immediately without requiring website reinstallation.
                </Alert>

                <Accordion defaultExpanded sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CodeIcon />
                      <Typography>Installation Code</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Add this script to your website's HTML. It will automatically load all enabled tags.
                    </Typography>
                    <Box sx={{ position: "relative" }}>
                      <CodeBlock
                        language="html"
                        text={getInstallScriptForPixel(selectedPixel)}
                        wrapLines
                        codeBlock
                        showLineNumbers={false}
                        theme={dracula}
                      />
                      <IconButton
                        onClick={() => handleCopy(getInstallScriptForPixel(selectedPixel))}
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          bgcolor: "rgba(255,255,255,0.1)",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                        }}
                      >
                        <ContentCopyIcon fontSize="small" sx={{ color: "white" }} />
                      </IconButton>
                    </Box>
                    {copied && (
                      <Typography variant="caption" color="primary" sx={{ display: "block", mt: 1 }}>
                        Copied to clipboard!
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="subtitle1">Tags ({tags.length})</Typography>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => setShowTagForm(true)}
                    variant="outlined"
                    size="small"
                  >
                    Add Tag
                  </Button>
                </Box>

                {loadingTags ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : tags.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="text.secondary">
                      No tags added yet. Add your first tag to get started.
                    </Typography>
                    <Button
                      startIcon={<AddIcon />}
                      variant="contained"
                      sx={{ mt: 2 }}
                      onClick={() => setShowTagForm(true)}
                    >
                      Add Your First Tag
                    </Button>
                  </Box>
                ) : (
                  <List>
                    {tags.map((tag) => (
                      <ListItem
                        key={tag.id}
                        sx={{
                          border: 1,
                          borderColor: tag.enabled ? "primary.main" : "divider",
                          borderRadius: 1,
                          mb: 1,
                          opacity: tag.enabled ? 1 : 0.6,
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              {tag.name}
                              <Chip
                                size="small"
                                label={getTagTypeLabel(tag.tagType)}
                                variant="outlined"
                              />
                              <Chip
                                size="small"
                                label={getPositionLabel(tag.position)}
                                variant="outlined"
                              />
                              {!tag.enabled && (
                                <Chip size="small" label="Disabled" color="default" />
                              )}
                            </Box>
                          }
                          secondary={
                            tag.isUrl
                              ? tag.content
                              : `${tag.content.substring(0, 80)}${tag.content.length > 80 ? "..." : ""}`
                          }
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title={tag.enabled ? "Disable" : "Enable"}>
                            <IconButton
                              onClick={() => handleToggleTagEnabled(tag)}
                              size="small"
                            >
                              {tag.enabled ? <PauseIcon /> : <PlayArrowIcon />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              onClick={() => handleEditTag(tag)}
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              onClick={() => setDeleteConfirmTag(tag)}
                              size="small"
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            )}
          </>
        ) : (
          // Main Pixel Dialog View
          <>
            <Tabs
              value={currentTab}
              onChange={(_, newValue) => setCurrentTab(newValue)}
              sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label="Create New" />
              <Tab label="Manage Pixels" />
            </Tabs>

            {currentTab === 0 && (
              <>
                {!pixelData ? (
                  <>
                    <Typography sx={{ mb: 2 }}>
                      Create a tracking pixel to capture website visitor information
                      and automatically add leads to your list "{listName}".
                    </Typography>

                    <Alert severity="info" sx={{ mb: 2 }}>
                      <strong>Tip:</strong> Create a dedicated smart list for pixel leads so they can be easily distinguished from leads added through other sources.
                    </Alert>

                    <TextField
                      label="Website Name"
                      placeholder="My Website"
                      value={websiteName}
                      onChange={(e) => setWebsiteName(e.target.value)}
                      fullWidth
                      margin="normal"
                      required
                    />

                    <TextField
                      label="Website URL"
                      placeholder="https://example.com"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      fullWidth
                      margin="normal"
                      required
                      type="url"
                    />

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2 }}
                    >
                      Webhook URL that will receive pixel data:
                    </Typography>
                    <Box
                      sx={{
                        mt: 1,
                        p: 1,
                        borderRadius: 1,
                        wordBreak: "break-all",
                        fontSize: "0.875rem",
                        fontFamily: "monospace",
                      }}
                    >
                      {webhookUrl}
                    </Box>

                    {error && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                      </Alert>
                    )}
                  </>
                ) : (
                  <>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Your pixel has been created successfully!
                    </Alert>

                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mt: 2 }}
                    >
                      Pixel ID
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      <Box
                        sx={{
                          flexGrow: 1,
                          p: 1,
                          borderRadius: 1,
                          fontFamily: "monospace",
                          fontSize: "0.875rem",
                        }}
                      >
                        {pixelData.id}
                      </Box>
                      <IconButton
                        onClick={() => handleCopy(pixelData.id)}
                        size="small"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mt: 3 }}
                    >
                      Installation Code
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Add this script tag to your website's HTML, preferably in the{" "}
                      <code>&lt;head&gt;</code> section:
                    </Typography>

                    <Box sx={{ position: "relative" }}>
                      <CodeBlock
                        language="html"
                        text={getInstallScript()}
                        wrapLines
                        codeBlock
                        showLineNumbers={false}
                        theme={dracula}
                      />
                      <IconButton
                        onClick={() => handleCopy(getInstallScript())}
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          bgcolor: "rgba(255,255,255,0.1)",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                        }}
                      >
                        <ContentCopyIcon fontSize="small" sx={{ color: "white" }} />
                      </IconButton>
                    </Box>

                    {copied && (
                      <Typography
                        variant="caption"
                        color="primary"
                        sx={{ display: "block", mt: 1 }}
                      >
                        Copied to clipboard!
                      </Typography>
                    )}

                    <Typography variant="body2" sx={{ mt: 3 }}>
                      Once installed, visitor data will automatically be sent to
                      your list. New columns will be created as needed.
                    </Typography>
                  </>
                )}
              </>
            )}

            {currentTab === 1 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  All pixels for your company. Pixels linked to this list will send
                  leads here.
                </Typography>
                {loadingPixels ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : pixelsError ? (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {pixelsError}
                  </Alert>
                ) : pixels.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="text.secondary">
                      No pixels created yet.
                    </Typography>
                    <Button
                      variant="outlined"
                      sx={{ mt: 2 }}
                      onClick={() => setCurrentTab(0)}
                    >
                      Create Your First Pixel
                    </Button>
                  </Box>
                ) : (
                  <List>
                    {pixels.map((pixel) => (
                      <ListItem
                        key={pixel.id}
                        sx={{
                          border: 1,
                          borderColor:
                            pixel.listId === listId ? "primary.main" : "divider",
                          borderRadius: 1,
                          mb: 1,
                          flexDirection: "column",
                          alignItems: "flex-start",
                          bgcolor:
                            pixel.listId === listId
                              ? "action.selected"
                              : "transparent",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            width: "100%",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box
                                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                              >
                                {pixel.websiteName}
                                <Chip
                                  size="small"
                                  label={pixel.status}
                                  color={
                                    pixel.status === PIXEL_STATUS_ACTIVE
                                      ? "primary"
                                      : "default"
                                  }
                                />
                                {pixel.listId === listId && (
                                  <Chip
                                    size="small"
                                    label="Linked to this list"
                                    color="primary"
                                    variant="outlined"
                                    icon={<LinkIcon />}
                                  />
                                )}
                                {pixel.listId && pixel.listId !== listId && (
                                  <Chip
                                    size="small"
                                    label={`Linked to: ${pixel.listName || "Unknown list"}`}
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box component="span">
                                <Typography
                                  component="span"
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  {pixel.websiteUrl}
                                  <IconButton
                                    size="small"
                                    href={/^https?:\/\/[a-zA-Z0-9]/.test(pixel.websiteUrl) ? pixel.websiteUrl : "#"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <OpenInNewIcon fontSize="small" />
                                  </IconButton>
                                </Typography>
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Tooltip title="Manage Tags">
                              <IconButton
                                onClick={() => handleManageTags(pixel)}
                                size="small"
                                color="primary"
                              >
                                <CodeIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                pixel.listId === listId
                                  ? "Unlink from this list"
                                  : "Link to this list"
                              }
                            >
                              <IconButton
                                onClick={() => handleLinkToList(pixel)}
                                size="small"
                                color={
                                  pixel.listId === listId ? "primary" : "default"
                                }
                              >
                                {pixel.listId === listId ? (
                                  <LinkOffIcon />
                                ) : (
                                  <LinkIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                pixel.status === PIXEL_STATUS_ACTIVE
                                  ? "Pause"
                                  : "Activate"
                              }
                            >
                              <IconButton
                                onClick={() => handleToggleStatus(pixel)}
                                size="small"
                              >
                                {pixel.status === PIXEL_STATUS_ACTIVE ? (
                                  <PauseIcon />
                                ) : (
                                  <PlayArrowIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Copy install code">
                              <IconButton
                                onClick={() =>
                                  handleCopy(getInstallScriptForPixel(pixel))
                                }
                                size="small"
                              >
                                <ContentCopyIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                onClick={() => setDeleteConfirmPixel(pixel)}
                                size="small"
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </ListItemSecondaryAction>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 3,
                            width: "100%",
                            pl: 2,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Leads: {pixel.leadCount}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Hits: {pixel.hitCount}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Last activity: {formatDate(pixel.lastActivityAt)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Created: {formatDate(pixel.createdDate)}
                          </Typography>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                )}

                {copied && (
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ display: "block", mt: 1 }}
                  >
                    Copied to clipboard!
                  </Typography>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {selectedPixel ? (
          <Button onClick={handleBackToPixels}>Back to Pixels</Button>
        ) : (
          <>
            <Button onClick={handleClose}>
              {pixelData ? "Close" : "Cancel"}
            </Button>
            {currentTab === 0 && !pixelData && (
              <Button
                onClick={handleCreate}
                variant="contained"
                disabled={loading || !websiteName.trim() || !websiteUrl.trim()}
              >
                {loading ? <CircularProgress size={24} /> : "Create Pixel"}
              </Button>
            )}
          </>
        )}
      </DialogActions>

      {/* Delete Pixel Confirmation Dialog */}
      <Dialog
        open={deleteConfirmPixel !== null}
        onClose={() => setDeleteConfirmPixel(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Pixel</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the pixel for "{deleteConfirmPixel?.websiteName}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmPixel(null)}>Cancel</Button>
          <Button
            onClick={() => deleteConfirmPixel && handleDeletePixel(deleteConfirmPixel)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Tag Confirmation Dialog */}
      <Dialog
        open={deleteConfirmTag !== null}
        onClose={() => setDeleteConfirmTag(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Tag</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the tag "{deleteConfirmTag?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmTag(null)}>Cancel</Button>
          <Button
            onClick={() => deleteConfirmTag && handleDeleteTag(deleteConfirmTag)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default PixelDialog;
